import {
  ApplicationConfig,
  ErrorHandler,
  provideAppInitializer,
  inject,
  Injector
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { provideClientHydration } from '@angular/platform-browser';
import {
  provideHttpClient,
  withFetch,
  withInterceptors
} from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideAuth0 } from '@auth0/auth0-angular';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef
} from 'primeng/dynamicdialog';

import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { initializeApp, preloadLanguage } from './app.initializer';

import { httpHeaderInterceptor } from './interceptors/http-header.interceptor';
import { TranslocoHttpLoader } from './services/transloco-http-loader.service';
import { LANGS } from './constants/lang.const';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { provideTranslocoPersistLang } from '@jsverse/transloco-persist-lang';
import { PrimeNG, providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import { httpErrorInterceptor } from './interceptors/http-error.interceptor';
import * as Sentry from '@sentry/angular';

export const TRANSLOCO_CONFIG = {
  config: {
    availableLangs: [...LANGS],
    defaultLang: 'engb',
    fallbackLang: 'engb',
    // Remove this option if your application doesn't support changing language in runtime.
    reRenderOnLangChange: true,
    prodMode: environment.production
  },
  loader: TranslocoHttpLoader
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideClientHydration(),
    provideHttpClient(
      withInterceptors([httpHeaderInterceptor, httpErrorInterceptor]),
      withFetch()
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        options: {
          prefix: 'p',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng, app-styles'
          },
          darkModeSelector: 'none'
        }
      }
    }),
    provideAppInitializer(async () => {
      const translocoService = inject(TranslocoService);
      const injector = inject(Injector);
      const primeNG = inject(PrimeNG);
      const initializeSession = initializeApp(injector);

      try {
        const { default: Aura } = await import('@primeuix/themes/aura');
        primeNG.theme.set({ ...primeNG.theme(), preset: definePreset(Aura) });
        await initializeSession();

        await preloadLanguage(translocoService)();
      } catch (error) {
        console.error('Error during app initialization:', error);
        throw error;
      }
    }),
    provideAuth0(environment.supplierPortalAuth0),
    provideTransloco(TRANSLOCO_CONFIG),
    provideTranslocoPersistLang({
      storage: {
        useValue: localStorage
      }
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    MessageService,
    ConfirmationService,
    DialogService,
    DynamicDialogConfig,
    DynamicDialogRef
  ]
};
